import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaMagic, FaPaperPlane, FaFileUpload, FaLink, FaTrash, FaCheck, FaSpinner, FaRobot } from 'react-icons/fa';
import './ConnectorAssistant.css';

// Configuration assistant (docs/data-sources-v2-spec.md §7): the side panel of
// the data-source paths. Documents in (URL, PDF, OpenAPI, text, screenshot),
// proposals, explanations of failed tests and answers out — every change
// comes back as a patch the page applies with one click.

const API = '/api/integrations/connectors/assistant';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

const readFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    if (file.type === 'application/pdf' || file.type.startsWith('image/')) reader.readAsDataURL(file); else reader.readAsText(file);
});

const ConnectorAssistant = ({ draftId, draft, lastTest, onApply, stepHint }) => {
    const { t } = useTranslation();
    const [documents, setDocuments] = useState([]);
    const [url, setUrl] = useState('');
    const [text, setText] = useState('');
    const [messages, setMessages] = useState([]);
    const [question, setQuestion] = useState('');
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const fileRef = useRef(null);
    const endRef = useRef(null);

    const loadDocuments = useCallback(async () => {
        if (!draftId) return;
        try {
            const res = await axios.get(`${API}/documents?draft_id=${encodeURIComponent(draftId)}`, authHeaders());
            setDocuments(res.data.documents || []);
        } catch (e) { setDocuments([]); }
    }, [draftId]);

    useEffect(() => { loadDocuments(); }, [loadDocuments]);
    useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [messages]);

    const fail = (e, fallback) => { const d = e?.response?.data?.detail; setError(typeof d === 'string' ? d : fallback); };
    const push = (message) => setMessages((m) => [...m, message]);

    const addDocument = async (payload) => {
        setBusy('doc');
        setError(null);
        try {
            const res = await axios.post(`${API}/documents`, { draft_id: draftId, ...payload }, authHeaders());
            setDocuments((d) => [...d, res.data]);
            push({ role: 'assistant', text: t('assistant.docAdded', 'Document lu : « {{title}} » ({{n}} extraits). Je peux proposer la configuration.', { title: res.data.title, n: res.data.chunks }) });
        } catch (e) {
            fail(e, t('assistant.docError', 'Le document n’a pas pu être lu.'));
        } finally {
            setBusy(null);
        }
    };

    const onFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        try {
            const content = await readFile(file);
            if (file.type === 'application/pdf') await addDocument({ kind: 'pdf', file_b64: content.split(',')[1], filename: file.name });
            else if (file.type.startsWith('image/')) await addDocument({ kind: 'image', image_b64: content, filename: file.name });
            else await addDocument({ kind: 'text', text: content, filename: file.name });
        } catch (e) {
            setError(t('assistant.docError', 'Le document n’a pas pu être lu.'));
        } finally {
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const removeDocument = async (id) => {
        try { await axios.delete(`${API}/documents/${encodeURIComponent(id)}`, authHeaders()); setDocuments((d) => d.filter((x) => x.id !== id)); } catch (e) { /* stays listed */ }
    };

    const propose = async () => {
        setBusy('propose');
        setError(null);
        try {
            const res = await axios.post(`${API}/propose`, { draft_id: draftId, draft }, authHeaders());
            const count = Object.keys(res.data.patch || {}).length;
            push({ role: 'assistant', text: count ? t('assistant.proposalReady', 'Configuration proposée à partir de {{docs}} ({{n}} réglages).', { docs: (res.data.sources || []).join(', '), n: count }) : t('assistant.proposalEmpty', 'Je n’ai rien trouvé d’exploitable dans les documents.'),
                notes: res.data.notes, errors: res.data.errors, patch: count ? res.data.patch : null, sample: res.data.sample ?? null });
        } catch (e) {
            fail(e, t('assistant.proposeError', 'La proposition a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const explain = useCallback(async (test) => {
        setBusy('explain');
        setError(null);
        try {
            const res = await axios.post(`${API}/explain`, { draft_id: draftId, draft, reason: test.reason, error: test.error, status: test.status, identifiers: test.identifiers || {}, raw: test.raw ?? null, steps: test.steps || null }, authHeaders());
            push({ role: 'assistant', text: res.data.explanation, patch: res.data.fix || null, confidence: res.data.confidence });
        } catch (e) {
            fail(e, t('assistant.explainError', 'L’explication a échoué.'));
        } finally {
            setBusy(null);
        }
    }, [draftId, draft, t]);

    // A failed test just happened on the page: explain it without being asked.
    const lastExplained = useRef(null);
    useEffect(() => {
        if (lastTest && lastTest.ok === false && lastTest.reason && lastTest.stamp !== lastExplained.current) {
            lastExplained.current = lastTest.stamp;
            explain(lastTest);
        }
    }, [lastTest, explain]);

    const ask = async () => {
        const q = question.trim();
        if (!q) return;
        setQuestion('');
        push({ role: 'user', text: q });
        setBusy('chat');
        setError(null);
        try {
            const history = messages.slice(-6).map((m) => ({ role: m.role, text: m.text }));
            const res = await axios.post(`${API}/chat`, { draft_id: draftId, question: q, draft, last_test: lastTest ? { found: lastTest.found, reason: lastTest.reason, error: lastTest.error, status: lastTest.status } : null, history }, authHeaders());
            push({ role: 'assistant', text: res.data.answer, patch: res.data.patch || null, sources: res.data.sources });
        } catch (e) {
            fail(e, t('assistant.chatError', 'Je n’ai pas pu répondre.'));
        } finally {
            setBusy(null);
        }
    };

    const apply = (index) => {
        const message = messages[index];
        onApply?.(message.patch, message.sample ?? null);
        setMessages((m) => m.map((x, i) => (i === index ? { ...x, applied: true } : x)));
    };

    return (
        <aside className="ca-panel">
            <div className="ca-head"><FaRobot /> <strong>{t('assistant.title', 'Assistant')}</strong> <span className="ca-muted">{stepHint}</span></div>
            <div className="ca-docs">
                <div className="ca-docs-head">{t('assistant.docs', 'Documentation')} <span className="ca-muted">{t('assistant.docsHint', 'URL, PDF, OpenAPI, texte, capture d’écran')}</span></div>
                {documents.map((doc) => (
                    <div key={doc.id} className="ca-doc"><span className="ca-doc-kind">{doc.kind}</span> <span className="ca-doc-title" title={doc.url || doc.title}>{doc.title}</span>
                        <button type="button" className="ca-link" onClick={() => removeDocument(doc.id)} title={t('assistant.removeDoc', 'Retirer')}><FaTrash /></button></div>
                ))}
                <div className="ca-row">
                    <input className="ca-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.mon-wms.com/api" />
                    <button type="button" className="ca-btn" disabled={busy === 'doc' || !url.trim() || !draftId} onClick={() => { addDocument({ kind: 'url', url: url.trim() }); setUrl(''); }}><FaLink /></button>
                    <input ref={fileRef} type="file" accept=".pdf,.json,.yaml,.yml,.txt,.md,image/*" hidden onChange={onFile} />
                    <button type="button" className="ca-btn" disabled={busy === 'doc' || !draftId} onClick={() => fileRef.current?.click()} title={t('assistant.upload', 'Déposer un fichier (PDF, OpenAPI, capture)')}><FaFileUpload /></button>
                </div>
                <textarea className="ca-textarea" value={text} onChange={(e) => setText(e.target.value)} placeholder={t('assistant.pastePlaceholder', 'Ou collez ici un extrait de documentation, une commande cURL ou une réponse d’exemple')} spellCheck={false} />
                <div className="ca-row">
                    <button type="button" className="ca-btn" disabled={busy === 'doc' || !text.trim() || !draftId} onClick={() => { addDocument({ kind: 'text', text }); setText(''); }}>{t('assistant.addText', 'Ajouter')}</button>
                    <button type="button" className="ca-btn ca-btn-primary" disabled={busy === 'propose' || documents.length === 0} onClick={propose}><FaMagic /> {t('assistant.propose', 'Proposer la configuration')}</button>
                </div>
            </div>
            {error && <div className="ca-error">{error}</div>}
            <div className="ca-messages">
                {messages.length === 0 && <p className="ca-muted">{t('assistant.welcome', 'Déposez la documentation de votre système et je remplis la configuration. Posez-moi vos questions : « pourquoi le statut n’est-il pas rempli ? », « comment chercher par e-mail ? »')}</p>}
                {messages.map((m, i) => (
                    <div key={i} className={`ca-msg ca-msg-${m.role}`}>
                        <div>{m.text}</div>
                        {m.notes?.length > 0 && <ul className="ca-notes">{m.notes.map((n, j) => <li key={j}>{n}</li>)}</ul>}
                        {m.errors?.length > 0 && <div className="ca-error">{m.errors.join(' · ')}</div>}
                        {m.sources?.length > 0 && <div className="ca-muted">{t('assistant.sources', 'D’après')} : {m.sources.join(', ')}</div>}
                        {m.patch && (
                            <div className="ca-patch">
                                <code>{Object.keys(m.patch).map((k) => k.replace(/^spec\.steps\.0\./, '')).join(', ')}</code>
                                {m.applied ? <span className="ca-applied"><FaCheck /> {t('assistant.applied', 'appliqué')}</span>
                                    : <button type="button" className="ca-btn ca-btn-primary" onClick={() => apply(i)}><FaCheck /> {t('assistant.apply', 'Appliquer')}</button>}
                            </div>
                        )}
                    </div>
                ))}
                {busy && busy !== 'doc' && <div className="ca-muted"><FaSpinner className="ca-spin" /> {t('assistant.thinking', 'Je lis…')}</div>}
                <div ref={endRef} />
            </div>
            <div className="ca-ask">
                <input className="ca-input" value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') ask(); }} placeholder={t('assistant.askPlaceholder', 'Une question sur cette source ?')} />
                <button type="button" className="ca-btn" disabled={busy === 'chat' || !question.trim()} onClick={ask}><FaPaperPlane /></button>
            </div>
        </aside>
    );
};

export default ConnectorAssistant;
